import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Assignment as PendingIcon,
  BugReport as BugIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PlaylistAddCheckCircle as DoneIcon,
  RocketLaunch as FeatureIcon
} from '@mui/icons-material';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import adminService from '../../services/AdminService';
import { AdminAssignableOption, AdminKanbanBoard, AdminKanbanTask } from '../../types/admin/AdminTypes';
import MetricCard from './MetricCard';

interface AdminKanbanBoardProps {
  initialBoard?: AdminKanbanBoard | null;
  onBoardChanged?: () => void | Promise<void>;
}

interface TaskFormState {
  title: string;
  description: string;
  type: string;
  status: string;
}

type StatusGroup = Record<string, AdminKanbanTask[]>;

const defaultTaskForm: TaskFormState = {
  title: '',
  description: '',
  type: 'implementacao',
  status: 'aberta'
};

const typeOptions = [
  { value: 'implementacao', label: 'Implementações' },
  { value: 'bug', label: 'Bugs' },
  { value: 'pendencia_geral', label: 'Pendências Gerais' }
];

const statusOptions = [
  { value: 'aberta', label: 'TODO' },
  { value: 'em_andamento', label: 'DOING' },
  { value: 'concluida', label: 'DONE' }
];

const typeMeta: Record<string, { color: string; background: string; icon: React.ReactElement }> = {
  implementacao: {
    color: '#0f6bff',
    background: '#eef4ff',
    icon: <FeatureIcon sx={{ color: '#0f6bff' }} />
  },
  bug: {
    color: '#d64545',
    background: '#fff1f1',
    icon: <BugIcon sx={{ color: '#d64545' }} />
  },
  pendencia_geral: {
    color: '#8a5b00',
    background: '#fff8e8',
    icon: <PendingIcon sx={{ color: '#8a5b00' }} />
  }
};

const statusMeta: Record<string, { color: string; background: string; icon: React.ReactElement }> = {
  aberta: {
    color: '#5b6475',
    background: '#f5f7fb',
    icon: <PendingIcon sx={{ color: '#5b6475' }} />
  },
  em_andamento: {
    color: '#8a5b00',
    background: '#fff8e8',
    icon: <PendingIcon sx={{ color: '#8a5b00' }} />
  },
  concluida: {
    color: '#2e7d32',
    background: '#e8f5e9',
    icon: <DoneIcon sx={{ color: '#2e7d32' }} />
  }
};

const statusColorMap: Record<string, 'default' | 'success' | 'warning' | 'info'> = {
  aberta: 'default',
  em_andamento: 'warning',
  concluida: 'success'
};

const mergeAssignableOptions = (
  selected: AdminAssignableOption | null,
  options: AdminAssignableOption[]
): AdminAssignableOption[] => {
  const allOptions = selected ? [selected, ...options] : options;
  const unique = new Map<string, AdminAssignableOption>();

  allOptions.forEach((option) => {
    if (option?.id) {
      unique.set(option.id, option);
    }
  });

  return Array.from(unique.values());
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '-';
  }

  return new Date(value).toLocaleString('pt-BR');
};

const getStatusLabel = (status: string) => {
  return statusOptions.find((option) => option.value === status)?.label ?? status;
};

const buildStatusGroups = (tasks: AdminKanbanTask[]): StatusGroup => {
  const initialGroups: StatusGroup = {
    aberta: [],
    em_andamento: [],
    concluida: []
  };

  tasks.forEach((task) => {
    if (!initialGroups[task.status]) {
      initialGroups[task.status] = [];
    }
    initialGroups[task.status].push(task);
  });

  return initialGroups;
};

const flattenStatusGroups = (groups: StatusGroup) => {
  return statusOptions.flatMap((status) => groups[status.value] ?? []);
};

const recalculateBoardMetrics = (columns: AdminKanbanBoard['columns']) => {
  const totalTasks = columns.reduce((acc, column) => acc + column.tasks.length, 0);
  const completedTasks = columns.reduce(
    (acc, column) => acc + column.tasks.filter((task) => task.status === 'concluida').length,
    0
  );

  return {
    totalTasks,
    openTasks: totalTasks - completedTasks,
    completedTasks
  };
};

const moveTaskLocally = (
  board: AdminKanbanBoard,
  typeId: string,
  sourceStatus: string,
  sourceIndex: number,
  destinationStatus: string,
  destinationIndex: number
): AdminKanbanBoard => {
  const columns = board.columns.map((column) => ({
    ...column,
    tasks: [...column.tasks]
  }));

  const targetColumn = columns.find((column) => column.id === typeId);
  if (!targetColumn) {
    return board;
  }

  const groupedTasks = buildStatusGroups(targetColumn.tasks);
  const sourceTasks = [...(groupedTasks[sourceStatus] ?? [])];
  const destinationTasks = sourceStatus === destinationStatus
    ? sourceTasks
    : [...(groupedTasks[destinationStatus] ?? [])];

  const [movedTask] = sourceTasks.splice(sourceIndex, 1);
  if (!movedTask) {
    return board;
  }

  const updatedTask: AdminKanbanTask = {
    ...movedTask,
    status: destinationStatus,
    statusLabel: getStatusLabel(destinationStatus)
  };

  destinationTasks.splice(destinationIndex, 0, updatedTask);

  groupedTasks[sourceStatus] = sourceTasks;
  groupedTasks[destinationStatus] = destinationTasks;

  targetColumn.tasks = flattenStatusGroups(groupedTasks).map((task, index) => ({
    ...task,
    order: index
  }));
  targetColumn.total = targetColumn.tasks.length;

  return {
    ...board,
    columns,
    ...recalculateBoardMetrics(columns)
  };
};

const AdminKanbanBoardComponent: React.FC<AdminKanbanBoardProps> = ({ initialBoard, onBoardChanged }) => {
  const [board, setBoard] = useState<AdminKanbanBoard | null>(initialBoard ?? null);
  const [loading, setLoading] = useState(!initialBoard);
  const [activeType, setActiveType] = useState(initialBoard?.columns[0]?.id ?? 'implementacao');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingTask, setEditingTask] = useState<AdminKanbanTask | null>(null);
  const [taskForm, setTaskForm] = useState<TaskFormState>(defaultTaskForm);
  const [adminSearch, setAdminSearch] = useState('');
  const [adminOptions, setAdminOptions] = useState<AdminAssignableOption[]>([]);
  const [selectedResponsible, setSelectedResponsible] = useState<AdminAssignableOption | null>(null);

  useEffect(() => {
    if (initialBoard) {
      setBoard(initialBoard);
      setActiveType(initialBoard.columns[0]?.id ?? 'implementacao');
      setLoading(false);
      return;
    }

    void loadBoard();
  }, [initialBoard]);

  useEffect(() => {
    if (!dialogOpen) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      void loadAdmins(adminSearch);
    }, 250);

    return () => window.clearTimeout(timeoutId);
  }, [dialogOpen, adminSearch]);

  useEffect(() => {
    if (!board?.columns.length) {
      return;
    }

    const activeColumnExists = board.columns.some((column) => column.id === activeType);
    if (!activeColumnExists) {
      setActiveType(board.columns[0].id);
    }
  }, [board, activeType]);

  const responsibleOptions = useMemo(
    () => mergeAssignableOptions(selectedResponsible, adminOptions),
    [selectedResponsible, adminOptions]
  );

  const selectedTypeColumn = useMemo(() => {
    if (!board) {
      return null;
    }

    return board.columns.find((column) => column.id === activeType) ?? board.columns[0] ?? null;
  }, [board, activeType]);

  const statusColumns = useMemo(() => {
    const groupedTasks = buildStatusGroups(selectedTypeColumn?.tasks ?? []);

    return statusOptions.map((status) => ({
      id: status.value,
      title: status.label,
      total: groupedTasks[status.value]?.length ?? 0,
      tasks: groupedTasks[status.value] ?? []
    }));
  }, [selectedTypeColumn]);

  const loadBoard = async () => {
    try {
      setLoading(true);
      const response = await adminService.getKanbanBoard();
      setBoard(response);
      setActiveType((current) => response.columns.find((column) => column.id === current)?.id ?? response.columns[0]?.id ?? 'implementacao');
    } catch (error) {
      console.error('Erro ao carregar kanban administrativo:', error);
      toast.error('Erro ao carregar kanban administrativo');
    } finally {
      setLoading(false);
    }
  };

  const loadAdmins = async (search?: string) => {
    try {
      const response = await adminService.searchKanbanAdmins(search, 10);
      setAdminOptions(response);
    } catch (error) {
      console.error('Erro ao buscar admins para o kanban:', error);
    }
  };

  const notifyParent = async () => {
    if (onBoardChanged) {
      await onBoardChanged();
    }
  };

  const resetDialogState = () => {
    setDialogOpen(false);
    setEditingTask(null);
    setTaskForm(defaultTaskForm);
    setSelectedResponsible(null);
    setAdminSearch('');
    setAdminOptions([]);
  };

  const openCreateDialog = (status?: string) => {
    setEditingTask(null);
    setTaskForm({
      ...defaultTaskForm,
      type: selectedTypeColumn?.id ?? activeType,
      status: status ?? defaultTaskForm.status
    });
    setSelectedResponsible(null);
    setDialogOpen(true);
  };

  const openEditDialog = (task: AdminKanbanTask) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title,
      description: task.description ?? '',
      type: task.type,
      status: task.status
    });
    setSelectedResponsible(task.responsible ?? null);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!taskForm.title.trim()) {
      toast.warning('Informe um título para a tarefa');
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        title: taskForm.title.trim(),
        description: taskForm.description.trim() || undefined,
        type: taskForm.type,
        status: taskForm.status,
        responsibleAdminId: selectedResponsible?.id
      };

      if (editingTask) {
        await adminService.updateKanbanTask(editingTask.id, payload);
        toast.success('Tarefa atualizada');
      } else {
        await adminService.createKanbanTask(payload);
        toast.success('Tarefa criada');
      }

      resetDialogState();
      await Promise.all([loadBoard(), notifyParent()]);
    } catch (error) {
      console.error('Erro ao salvar tarefa do kanban:', error);
      toast.error('Erro ao salvar tarefa');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (task: AdminKanbanTask) => {
    if (!window.confirm(`Excluir a tarefa "${task.title}"?`)) {
      return;
    }

    try {
      await adminService.deleteKanbanTask(task.id);
      toast.success('Tarefa removida');
      await Promise.all([loadBoard(), notifyParent()]);
    } catch (error) {
      console.error('Erro ao excluir tarefa do kanban:', error);
      toast.error('Erro ao excluir tarefa');
    }
  };

  const handleDragEnd = async (result: DropResult) => {
    if (!board || !selectedTypeColumn || !result.destination) {
      return;
    }

    const { source, destination, draggableId } = result;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const previousBoard = board;
    const originalTask = selectedTypeColumn.tasks.find((task) => task.id === draggableId);
    if (!originalTask) {
      return;
    }

    const nextBoard = moveTaskLocally(
      board,
      selectedTypeColumn.id,
      source.droppableId,
      source.index,
      destination.droppableId,
      destination.index
    );

    setBoard(nextBoard);

    try {
      if (originalTask.status !== destination.droppableId) {
        await adminService.updateKanbanTask(originalTask.id, {
          title: originalTask.title,
          description: originalTask.description ?? undefined,
          type: selectedTypeColumn.id,
          status: destination.droppableId,
          responsibleAdminId: originalTask.responsible?.id
        });
      }

      const nextTypeColumn = nextBoard.columns.find((column) => column.id === selectedTypeColumn.id);
      const destinationIndex = nextTypeColumn?.tasks.findIndex((task) => task.id === draggableId) ?? 0;

      await adminService.moveKanbanTask(draggableId, {
        type: selectedTypeColumn.id,
        destinationIndex
      });

      await Promise.all([loadBoard(), notifyParent()]);
    } catch (error) {
      console.error('Erro ao mover tarefa do kanban:', error);
      setBoard(previousBoard);
      toast.error('Erro ao mover tarefa');
    }
  };

  if (loading || !board || !selectedTypeColumn) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentTypeMeta = typeMeta[selectedTypeColumn.id] ?? typeMeta.implementacao;

  return (
    <Box>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <MetricCard
            title="Tarefas Totais"
            value={board.totalTasks}
            subtitle="Cards cadastrados no admin"
            icon={<FeatureIcon sx={{ color: '#0f6bff' }} />}
            iconBgColor="#eef4ff"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <MetricCard
            title="Em Aberto"
            value={board.openTasks}
            subtitle="TODO e DOING"
            icon={<PendingIcon sx={{ color: '#8a5b00' }} />}
            iconBgColor="#fff8e8"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <MetricCard
            title="Concluídas"
            value={board.completedTasks}
            subtitle="Cards finalizados"
            icon={<DoneIcon sx={{ color: '#2e7d32' }} />}
            iconBgColor="#e8f5e9"
          />
        </Grid>
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
            Kanban Administrativo
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cada tipo fica em uma aba e o fluxo da execução roda em TODO, DOING e DONE.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => openCreateDialog()}>
          Nova tarefa
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 1,
          borderRadius: 3,
          border: '1px solid #e0e0e0',
          mb: 3
        }}
      >
        <Tabs
          value={selectedTypeColumn.id}
          onChange={(_, value) => setActiveType(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 56,
              alignItems: 'flex-start'
            }
          }}
        >
          {board.columns.map((column) => {
            const meta = typeMeta[column.id] ?? typeMeta.implementacao;

            return (
              <Tab
                key={column.id}
                value={column.id}
                icon={meta.icon}
                iconPosition="start"
                label={`${column.title} (${column.total})`}
              />
            );
          })}
        </Tabs>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: '1px solid #e0e0e0',
          backgroundColor: currentTypeMeta.background,
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          {currentTypeMeta.icon}
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
            {selectedTypeColumn.title}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {selectedTypeColumn.total} tarefa(s) nesta aba. Arraste entre TODO, DOING e DONE para refletir o andamento.
        </Typography>
      </Paper>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Grid container spacing={3}>
          {statusColumns.map((column) => {
            const meta = statusMeta[column.id] ?? statusMeta.aberta;

            return (
              <Grid key={column.id} size={{ xs: 12, lg: 4 }}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: '1px solid #e0e0e0',
                    backgroundColor: meta.background,
                    minHeight: 520
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {meta.icon}
                      <Box>
                        <Typography sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                          {column.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {column.total} tarefa(s)
                        </Typography>
                      </Box>
                    </Box>
                    <Tooltip title={`Nova tarefa em ${column.title}`}>
                      <IconButton
                        size="small"
                        sx={{ backgroundColor: '#fff', '&:hover': { backgroundColor: '#fff' } }}
                        onClick={() => openCreateDialog(column.id)}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Droppable droppableId={column.id}>
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          minHeight: 420,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 1.5,
                          borderRadius: 2,
                          p: 0.5,
                          backgroundColor: snapshot.isDraggingOver ? 'rgba(255,255,255,0.6)' : 'transparent'
                        }}
                      >
                        {column.tasks.map((task, index) => (
                          <Draggable key={task.id} draggableId={task.id} index={index}>
                            {(draggableProvided, draggableSnapshot) => (
                              <Paper
                                ref={draggableProvided.innerRef}
                                {...draggableProvided.draggableProps}
                                {...draggableProvided.dragHandleProps}
                                elevation={0}
                                sx={{
                                  p: 2,
                                  borderRadius: 2.5,
                                  border: `1px solid ${meta.color}30`,
                                  backgroundColor: '#fff',
                                  boxShadow: draggableSnapshot.isDragging ? '0 10px 30px rgba(0,0,0,0.12)' : 'none'
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.75 }}>
                                      {task.title}
                                    </Typography>
                                    {task.description && (
                                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                        {task.description}
                                      </Typography>
                                    )}
                                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 1.5 }}>
                                      <Chip
                                        label={getStatusLabel(task.status)}
                                        size="small"
                                        color={statusColorMap[task.status] ?? 'default'}
                                        variant={task.status === 'aberta' ? 'outlined' : 'filled'}
                                      />
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary">
                                      Responsável: {task.responsible?.name ? `${task.responsible.name} (${task.responsible.login})` : 'Não definido'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                      Atualizado em {formatDateTime(task.updatedAt ?? task.createdAt)}
                                    </Typography>
                                  </Box>
                                  <Stack spacing={0.5}>
                                    <Tooltip title="Editar tarefa">
                                      <IconButton size="small" onClick={() => openEditDialog(task)}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Excluir tarefa">
                                      <IconButton size="small" color="error" onClick={() => handleDelete(task)}>
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </Box>
                              </Paper>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                        {column.tasks.length === 0 && (
                          <Paper
                            elevation={0}
                            sx={{
                              borderRadius: 2,
                              border: '1px dashed #cbd5e1',
                              backgroundColor: 'rgba(255,255,255,0.7)',
                              p: 3,
                              textAlign: 'center'
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              Nenhuma tarefa nesta etapa.
                            </Typography>
                          </Paper>
                        )}
                      </Box>
                    )}
                  </Droppable>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </DragDropContext>

      <Dialog open={dialogOpen} onClose={resetDialogState} fullWidth maxWidth="sm">
        <DialogTitle>{editingTask ? 'Editar tarefa' : 'Nova tarefa'}</DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          <Stack spacing={2}>
            <TextField
              label="Título"
              value={taskForm.title}
              onChange={(event) => setTaskForm((current) => ({ ...current, title: event.target.value }))}
              fullWidth
            />
            <TextField
              label="Descrição"
              value={taskForm.description}
              onChange={(event) => setTaskForm((current) => ({ ...current, description: event.target.value }))}
              fullWidth
              multiline
              rows={4}
            />
            <TextField
              select
              label="Tipo"
              value={taskForm.type}
              onChange={(event) => setTaskForm((current) => ({ ...current, type: event.target.value }))}
              fullWidth
            >
              {typeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Etapa"
              value={taskForm.status}
              onChange={(event) => setTaskForm((current) => ({ ...current, status: event.target.value }))}
              fullWidth
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
            <Autocomplete
              options={responsibleOptions}
              value={selectedResponsible}
              onChange={(_, value) => setSelectedResponsible(value)}
              onInputChange={(_, value) => setAdminSearch(value)}
              getOptionLabel={(option) => `${option.name} (${option.login})`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Responsável"
                  placeholder="Buscar admin por login"
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetDialogState}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? 'Salvando...' : editingTask ? 'Salvar' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminKanbanBoardComponent;
